import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Spinner, toast } from "@heroui/react";
import { RefreshCw } from "lucide-react";
import { ERROR_CODE, TemplatePtAPI, type SendReceiveRecord } from "@repo/api";
import { UserAPI } from "@repo/api/user";
import { useQueryClient } from "@tanstack/react-query";
import { storageKeys, templateKeys } from "@repo/query";
import { formatTime } from "@repo/utils/tools";
import { UserAvatar } from "../../../components/User";

type UserBrief = {
  nickname: string;
  avatar_url: string;
};

type DeleteTemplateBlockedModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  onDeleted: () => void;
};

const PAGE_SIZE = 100;

function getErrorInfo(error: unknown): { status?: number; msg?: string } {
  if (error && typeof error === "object") {
    const maybe = error as { status?: number; msg?: string };
    return { status: maybe.status, msg: maybe.msg };
  }
  return {};
}

function isBlockingSend(item: SendReceiveRecord, retracted: boolean) {
  if (retracted) {
    return false;
  }
  return !item.is_received || item.mask_status === 0;
}

async function fetchBlockingSends(templateId: string): Promise<SendReceiveRecord[]> {
  const records: SendReceiveRecord[] = [];
  let page = 1;
  for (; ;) {
    const { data } = await TemplatePtAPI.getSendReceiveTemplates(
      page,
      PAGE_SIZE,
      "send",
      undefined,
      undefined,
      undefined,
      templateId,
    );
    const content = data.content ?? [];
    records.push(...content);
    const totalPages = data.total_pages || 0;
    if (content.length === 0 || (totalPages > 0 ? page >= totalPages : content.length < PAGE_SIZE)) {
      break;
    }
    page += 1;
  }
  return records.filter((item) => !item.is_received || item.mask_status === 0);
}

async function fetchUserBriefMap(records: SendReceiveRecord[]) {
  const uniqueUids = Array.from(new Set(records.map((item) => item.receiver_uid).filter(Boolean)));
  if (uniqueUids.length === 0) {
    return new Map<string, UserBrief>();
  }
  try {
    const { data } = await UserAPI.batchQueryByUIDs(uniqueUids);
    return new Map(
      data.hit.map((user) => [
        user.uid,
        {
          nickname: user.nickname || user.uid,
          avatar_url: user.avatar_url || "",
        },
      ]),
    );
  } catch {
    return new Map<string, UserBrief>();
  }
}

export default function DeleteTemplateBlockedModal({
  isOpen,
  onOpenChange,
  templateId,
  onDeleted,
}: DeleteTemplateBlockedModalProps) {
  const [items, setItems] = useState<SendReceiveRecord[]>([]);
  const [userMap, setUserMap] = useState<Map<string, UserBrief>>(() => new Map());
  const [retractedIds, setRetractedIds] = useState<Set<number>>(() => new Set());
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retractingId, setRetractingId] = useState<number | null>(null);
  // 撤回 / 删除成功后让详情、列表、发货记录缓存失效，父页面无需再手动刷新
  const queryClient = useQueryClient();
  const invalidateTemplates = () => {
    void queryClient.invalidateQueries({ queryKey: templateKeys.all });
  };

  const blockingCount = useMemo(
    () => items.filter((item) => isBlockingSend(item, retractedIds.has(item.id))).length,
    [items, retractedIds],
  );

  const loadList = useCallback(async () => {
    if (!templateId) {
      return;
    }
    setLoading(true);
    try {
      const records = await fetchBlockingSends(templateId);
      const users = await fetchUserBriefMap(records);
      setItems(records);
      setUserMap(users);
      setRetractedIds(new Set());
    } catch (error: unknown) {
      toast.danger(getErrorInfo(error).msg || "加载未签收记录失败");
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      void loadList();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, loadList]);

  const handleRetract = async (recordId: number) => {
    setRetractingId(recordId);
    try {
      await TemplatePtAPI.retract(recordId);
      setRetractedIds((prev) => {
        const next = new Set(prev);
        next.add(recordId);
        return next;
      });
      invalidateTemplates();
    } catch (error: unknown) {
      toast.danger(getErrorInfo(error).msg || "撤回失败，请重试");
    } finally {
      setRetractingId(null);
    }
  };

  const handleRetry = async () => {
    if (!templateId || blockingCount > 0) {
      return;
    }
    setRetrying(true);
    try {
      await TemplatePtAPI.deleteTemplate(templateId);
      toast.success("删除成功");
      invalidateTemplates();
      void queryClient.invalidateQueries({ queryKey: storageKeys.quota() });
      onOpenChange(false);
      onDeleted();
    } catch (error: unknown) {
      const info = getErrorInfo(error);
      if (info.status === ERROR_CODE.HAS_UNRECEIVED_SENDS) {
        await loadList();
        return;
      }
      toast.danger(info.msg || "删除失败，请重试");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <Modal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal.Container size="lg">
        <Modal.Dialog>
          {({ close }) => (
            <>
              <Modal.Header className="flex items-center flex-row justify-between gap-3">
                <Modal.Heading>需要进一步操作</Modal.Heading>
                <Button
                  variant="ghost"
                  aria-label="刷新"
                  isDisabled={loading || retrying}
                  onPress={() => void loadList()}
                >
                  <RefreshCw className={loading ? "size-4 animate-spin" : "size-4"} />
                  <span className="sr-only">刷新</span>
                </Button>
              </Modal.Header>
              <Modal.Body className="flex flex-col gap-4">
                <p className="text-sm text-gray-700">
                  当前的模板有未签收的收货人，避免删除后导致收货人无法签收，您需要撤回未签收的发货才可删除该模板。
                </p>
                {loading ? (
                  <div className="flex items-center justify-center py-10">
                    <Spinner />
                  </div>
                ) : items.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-500">暂无未签收或进行中的发货</div>
                ) : (
                  <div className="max-h-80 overflow-y-auto divide-y divide-[#f3f3f3] rounded-lg border border-[#f3f3f3]">
                    {items.map((item) => {
                      const retracted = retractedIds.has(item.id);
                      const user = userMap.get(item.receiver_uid);
                      const nickname = user?.nickname || item.receiver_uid || "-";
                      const inProgress = item.is_received && item.mask_status === 0 && !retracted;
                      return (
                        <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                          <UserAvatar avatar_url={user?.avatar_url} size="sm" />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-medium text-gray-900">{nickname}</div>
                            <div className="truncate text-xs text-gray-500">{item.receiver_uid}</div>
                            <div className="text-xs text-gray-400">
                              {item.created_at
                                ? formatTime(item.created_at, "YYYY-MM-DD HH:mm:ss")
                                : "-"}
                            </div>
                          </div>
                          {retracted ? (
                            <span className="shrink-0 text-sm text-gray-400">已撤回</span>
                          ) : inProgress ? (
                            <span className="shrink-0 text-sm text-orange-500">打标中</span>
                          ) : (
                            <Button
                              size="sm"
                              variant="danger"
                              className="shrink-0"
                              isPending={retractingId === item.id}
                              isDisabled={retractingId !== null && retractingId !== item.id}
                              onPress={() => void handleRetract(item.id)}
                            >
                              {({ isPending }) => (
                                <>
                                  {isPending && <Spinner size="sm" color="current" />}
                                  撤回
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button
                  className="bg-gray-100 text-[#333333] rounded-full"
                  onPress={close}
                  isDisabled={retrying}
                >
                  关闭
                </Button>
                <Button
                  className="bg-primary text-white rounded-full"
                  onPress={() => void handleRetry()}
                  isPending={retrying}
                  isDisabled={loading || blockingCount > 0}
                >
                  {({ isPending }) => (
                    <>
                      {isPending && <Spinner size="sm" color="current" />}
                      重试
                    </>
                  )}
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  );
}
